import { useSelector } from "react-redux";
import { IProduct, IProductCategory, IStore } from "@snackpass/snackpass-types";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
    setDashboardFilterTransactionChannelOptions,
    setActiveProductCategory,
    setActiveStore,
    setActiveStoreIdSliceId,
    setIsLoading,
    setLegacyProducts,
    getActiveStoreIdSliceId,
    clearActiveStoreUnreads,
    clearActiveStore,
} from "src/redux/slices";
import api from "src/api/rest";
import { getUser } from "src/redux/selectors";
import { multiMenuThunks } from "#menu-editor/multi-menus/redux/thunks";
import { sendError, logAndSendError } from "src/utils/errors";
import { useAppDispatch } from "src/redux/hooks";
import { fetchStoreProducts } from "#menu/api";
import { Provider } from "#sales-report/types";
import { clearPersistedActiveStoreId } from "#hooks/initial/use-persist-store-id";
import { Routes } from "#navigation/routes";

export const useSyncActiveStore = () => {
    const activeStoreId = useSelector(getActiveStoreIdSliceId);
    const dispatch = useAppDispatch();
    const user = useSelector(getUser);
    const navigate = useNavigate();

    const getStore = async (storeId: string): Promise<IStore | undefined> => {
        try {
            const res = await api.stores.getOne(storeId);
            return res.data.store;
        } catch (err) {
            toast.error("failed to get store");
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    const getStoreProducts = async (
        storeId: string,
    ): Promise<IProduct[] | undefined> => {
        try {
            return fetchStoreProducts(storeId).then((res) => res.products);
        } catch (err) {
            logAndSendError(err);
            toast.error("failed to get store products");
        }
    };

    const getProviders = async (
        storeId: string,
    ): Promise<Provider[] | undefined> => {
        try {
            return api.reports
                .getSalesChannels({ storeId })
                .then((res) => res.data?.channels);
        } catch (err) {
            logAndSendError(err);
            toast.error("failed to get purchase channels");
        }
    };

    const syncStore = useCallback(async () => {
        // checking for the user prevents a pre-emptive 403
        if (!activeStoreId || !user?._id) return;
        dispatch(setIsLoading(true));
        // Clear unread counts when switching stores
        dispatch(clearActiveStoreUnreads());
        try {
            const store = await getStore(activeStoreId);
            if (!store) {
                return;
            }
            if (store.isArchived) {
                const response = user.snackpassPermissions.isSnackpassEmployee
                    ? await api.stores.get()
                    : await api.stores.getMyAdminStores();
                const nextStore = (response.data.stores as IStore[]).find(
                    (store) => !store.isArchived,
                );
                if (!nextStore) {
                    dispatch(clearActiveStore());
                    clearPersistedActiveStoreId();
                    navigate(Routes.NotAllowed, { replace: true });
                }
                dispatch(setActiveStoreIdSliceId(nextStore?._id ?? ""));
                return;
            }
            dispatch(setActiveStore(store));

            const products = await getStoreProducts(activeStoreId);
            if (products) {
                dispatch(setLegacyProducts(products));
            }

            const providers = await getProviders(activeStoreId);
            dispatch(
                setDashboardFilterTransactionChannelOptions(providers ?? []),
            );

            const firstCategory: IProductCategory | undefined =
                store.productCategories[0];
            if (firstCategory) {
                dispatch(setActiveProductCategory(firstCategory.name));
            }
            void dispatch(multiMenuThunks.fetchStoreMultiMenus(activeStoreId));
        } catch (err) {
            sendError(err);
        }
        dispatch(setIsLoading(false));
    }, [activeStoreId, navigate, user?._id]);

    useEffect(() => {
        void syncStore();
    }, [syncStore]);
};
